import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NotificationsService } from 'api/notifications.api';
import { CeAlert, GetAlertsResponse, StatusApiService } from 'api/status.api';
import { Subject, firstValueFrom } from 'rxjs';
import { auditTime, concatMap, distinctUntilChanged, exhaustMap, filter, startWith, take } from 'rxjs/operators';
import { DialogLinkService } from 'shared/routed-dialog/dialog-link.service';
import { LocationsService } from 'shared/stations-service/locations.service';
import { StationsService } from 'shared/stations-service/stations.service';
import { retryWithDelay } from 'shared/utils/retryWithDelay';
import { AlertProblemService, SeverityIcon } from 'shared/alert-problem-service/alert-problem.service';
import { Severity } from '@kempoweroyj/ngx-error-codes-api-client';

export interface AlertsByStationIds {
    [stationId: string]: GetAlertsResponse['activeAlerts'];
}

@Component({
    selector: 'ce-tile-alerts',
    templateUrl: './tile-alerts.component.html',
    styleUrls: ['./tile-alerts.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
@UntilDestroy()
export class TileAlertsComponent implements OnInit {
    @Input() stationId: string;
    @Input() locationId: string;

    private _locationStationIds: string[];

    loading = true;

    alerts: CeAlert[] = [];
    displayAlerts: CeAlert[] = [];
    alertSeverities: Map<string, Severity> = new Map(); // Cache severities by alert ID
    MAX_ALERTS_SHOWN = 15; // don't show more than this...

    needsAttention = $localize`:Status tile header|:Needs attention`;
    statusOK = $localize`:Status tile header|:Status OK`;

    private onDestory$ = new Subject<void>();

    constructor(
        private notifications: NotificationsService,
        private cdRef: ChangeDetectorRef,
        private dialogLink: DialogLinkService,
        private statusService: StatusApiService,
        private readonly locationsService: LocationsService,
        private readonly stations: StationsService,
        private readonly alertProblemService: AlertProblemService
    ) {}

    async updateAlerts(alerts: CeAlert[]) {
        this.alerts = alerts;
        this.displayAlerts = alerts.slice(0, this.MAX_ALERTS_SHOWN);

        // Calculate severities for all display alerts
        await this.calculateAlertSeverities(this.displayAlerts);

        this.cdRef.markForCheck();
    }

    /**
     * Calculate and cache severities for the given alerts
     */
    private async calculateAlertSeverities(alerts: CeAlert[]) {
        // Clear existing cache for alerts not in current list
        const currentAlertIds = alerts.map((a) => a.id);
        for (const [id] of this.alertSeverities.entries()) {
            if (!currentAlertIds.includes(id)) {
                this.alertSeverities.delete(id);
            }
        }

        // Calculate severity for new alerts
        const promises = alerts.map(async (alert) => {
            if (!this.alertSeverities.has(alert.id)) {
                try {
                    const severity = await this.getSeverityForAlert(alert);
                    this.alertSeverities.set(alert.id, severity);
                } catch (error) {
                    console.error(`Failed to calculate severity for alert ${alert.id}:`, error);
                    this.alertSeverities.set(alert.id, 'unknown');
                }
            }
        });

        await Promise.all(promises);
    }

    /**
     * Determine severity for an alert using the same logic as alert-dialog
     */
    private async getSeverityForAlert(alert: CeAlert): Promise<Severity> {
        try {
            const vendorCode = 'kempower'; // Default vendor, could be enhanced to get from station data

            // Get station data if available for more accurate vendor determination
            let station = null;
            if (alert.stationId) {
                try {
                    station = await firstValueFrom(this.stations.station$(alert.stationId));
                } catch (error) {
                    console.warn(`Failed to get station data for ${alert.stationId}:`, error);
                }
            }

            const actualVendorCode = station?.station?.vendorName?.toLowerCase() || vendorCode;

            // Use the service to process the alert and get comprehensive error information
            const processedErrors = await this.alertProblemService.processAlertErrors(actualVendorCode, alert, station);

            // If there are child errors (from comma-separated codes or PMC devices), get highest severity
            if (processedErrors.childErrors && processedErrors.childErrors.length > 0) {
                return this.alertProblemService.getHighestSeverity(processedErrors.childErrors);
            }

            // Otherwise, return the main error severity
            return processedErrors.mainError.severity || 'unknown';
        } catch (error) {
            console.error(`Failed to calculate severity for alert ${alert.id}:`, error);
            return 'unknown';
        }
    }

    /**
     * Get severity for a specific alert from cache
     */
    getSeverityForAlertFromCache(alert: CeAlert): Severity {
        return this.alertSeverities.get(alert.id) || 'unknown';
    }

    /**
     * Get severity icon configuration for a given severity level
     */
    getSeverityIcon(severity: Severity): SeverityIcon {
        return this.alertProblemService.getSeverityIcon(severity);
    }

    /**
     * Get severity icon for a specific alert
     */
    getSeverityIconForAlert(alert: CeAlert): SeverityIcon {
        const severity = this.getSeverityForAlertFromCache(alert);
        return this.getSeverityIcon(severity);
    }

    async ngOnInit(): Promise<void> {
        // Initialize the alert problem service cache with default vendor
        // This will be enhanced per-alert with actual station vendor data in getSeverityForAlert
        this.alertProblemService
            .fetchAllErrorCodesForVendor('kempower')
            .then(() => {
                // Cache initialized successfully
            })
            .catch((_error) => {
                // Failed to initialize alert problem codes cache
            });

        // If we have a specific station, also try to initialize with its vendor
        if (this.stationId) {
            try {
                const station = await firstValueFrom(this.stations.station$(this.stationId));
                if (station?.station?.vendorName) {
                    await this.alertProblemService.fetchAllErrorCodesForVendor(
                        station.station.vendorName.toLowerCase()
                    );
                }
            } catch (error) {
                console.warn('Failed to initialize vendor-specific error codes cache:', error);
            }
        }

        if (this.locationId) {
            this.notifications.notification$
                .pipe(
                    filter(
                        (n: any) =>
                            n.type === 'ChargingStationUpdated' &&
                            n.changedSchemas.includes('alert') &&
                            (!this.locationId ||
                                n['locationUid'] === this.locationId ||
                                this._locationStationIds.includes(n.stationId))
                    ),
                    auditTime(20000),
                    startWith(0),
                    exhaustMap(() => this.statusService.getLocationAlerts(this.locationId)),
                    retryWithDelay(5000), // if failed, retry after 5s
                    untilDestroyed(this)
                )
                .subscribe(async (res) => {
                    this.loading = false;
                    const alerts = res?.activeAlerts?.filter((a) => !!a.id);
                    await this.updateAlerts(alerts);

                    this.cdRef.markForCheck();
                });

            this.locationsService
                .locationStations$(this.locationId)
                .pipe(
                    distinctUntilChanged(),
                    untilDestroyed(this),
                    take(1) // it's OK to not listen to changes here - how often do locations get more stations anyway?
                )
                .subscribe((ss) => {
                    // delete old station ids
                    this._locationStationIds = [];
                    this._locationStationIds = ss.map((s) => s.stationId);

                    // Subscribe to stations of location
                    this.notifications.connected$
                        .pipe(
                            filter((connected: any) => connected),
                            untilDestroyed(this),
                            take(1)
                        )
                        .subscribe(() => {
                            this.notifications.subscribeUntil(
                                { stationIds: this._locationStationIds, locationIds: [this.locationId] },
                                this.onDestory$
                            );
                        });
                });
        } else {
            // Subscribe to stations of location
            this.notifications.connected$
                .pipe(
                    filter((connected: any) => connected),
                    untilDestroyed(this),
                    take(1)
                )
                .subscribe(() => {
                    this.notifications.subscribeUntil({ stationIds: [this.stationId] }, this.onDestory$);
                });

            this.notifications.notification$
                .pipe(
                    filter(
                        (n: any) =>
                            n.type === 'ChargingStationUpdated' &&
                            n.changedSchemas.includes('alert') &&
                            (!this.stationId || n.stationId === this.stationId)
                    ),
                    auditTime(20000),
                    startWith(0),
                    untilDestroyed(this),
                    concatMap(() =>
                        !this.stationId
                            ? this.statusService.getAlerts()
                            : this.statusService.getStationAlerts(this.stationId)
                    )
                )
                .subscribe(async (res) => {
                    this.loading = false;
                    const alerts = res?.activeAlerts?.filter((a) => !!a.id) as GetAlertsResponse['activeAlerts'];
                    await this.updateAlerts(alerts);
                    this.cdRef.markForCheck();
                });
        }
    }

    ngOnDestroy() {
        this.onDestory$.next();
    }

    openAlertHistory() {
        if (this.locationId) {
            this.dialogLink.open(['alert-history', 'location', this.locationId]);
        } else if (this.stationId) {
            this.dialogLink.open(['alert-history', 'station', this.stationId]);
        }
    }

    trackByAlert(_idx: number, alert: CeAlert) {
        return alert.id;
    }
}
